
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { CheckCircle, XCircle, Clock, User, Calendar, FileText, Building2 } from "lucide-react";
import SubmissionEditor from "./SubmissionEditor";
import { supabase } from "@/integrations/supabase/client";

interface SubmissionDetailModalProps {
  submission: any;
  isOpen: boolean;
  onClose: () => void;
  onAction: (submissionId: string, action: string, feedback?: string) => Promise<void>;
}

const SubmissionDetailModal: React.FC<SubmissionDetailModalProps> = ({
  submission,
  isOpen,
  onClose,
  onAction
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [actionDialog, setActionDialog] = useState<{
    type: 'approve' | 'reject' | null;
    feedback: string;
  }>({ type: null, feedback: '' });
  const [editedSubmission, setEditedSubmission] = useState(submission);
  const [saving, setSaving] = useState(false);
  const [manufacturerSuggestions, setManufacturerSuggestions] = useState([]);
  const [loadingManufacturerSuggestions, setLoadingManufacturerSuggestions] = useState(false);

  // Fetch manufacturer suggestions if this is a printer submission
  useEffect(() => {
    if (submission?.type === 'printer' && submission?.id) {
      fetchManufacturerSuggestions();
    }
  }, [submission]);

  const fetchManufacturerSuggestions = async () => {
    try {
      setLoadingManufacturerSuggestions(true);
      console.log('🏭 Fetching manufacturer suggestions for submission:', submission.id);
      
      const { data, error } = await supabase
        .from("printer_manufacturer_suggestions")
        .select("*")
        .eq("submitted_by", submission.user_id)
        .order("created_at", { ascending: false });
      
      if (error) {
        console.error('Error fetching manufacturer suggestions:', error);
        return;
      }
      
      console.log('📋 Manufacturer suggestions fetched:', data?.length || 0, 'items');
      setManufacturerSuggestions(data || []);
      
    } catch (error) {
      console.error('Error in fetchManufacturerSuggestions:', error);
    } finally {
      setLoadingManufacturerSuggestions(false);
    }
  };

  const handleSaveEdits = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("submissions")
        .update({
          content: editedSubmission.content,
          updated_at: new Date().toISOString()
        })
        .eq("id", submission.id);

      if (error) throw error;

      toast.success("Changes saved successfully");
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving submission:', error);
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async (action: 'approve' | 'reject') => {
    try {
      await onAction(
        submission.id, 
        action === 'approve' ? 'approved' : 'rejected',
        actionDialog.feedback || null
      );
      setActionDialog({ type: null, feedback: '' });
      onClose();
    } catch (error) {
      console.error(`Error ${action}ing submission:`, error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "flex items-center gap-1";
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className={`${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`}>
          <Clock className="h-3 w-3" />
          Pending
        </Badge>;
      case 'approved':
        return <Badge variant="outline" className={`${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`}>
          <CheckCircle className="h-3 w-3" />
          Approved
        </Badge>;
      case 'rejected':
        return <Badge variant="outline" className={`${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`}>
          <XCircle className="h-3 w-3" />
          Rejected
        </Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {submission.content?.name || 'Untitled Submission'}
                </DialogTitle>
                <DialogDescription className="flex items-center gap-4 mt-2">
                  <span className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    User ID: {submission.user_id?.slice(0, 8)}...
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {formatDistanceToNow(new Date(submission.submitted_at), { addSuffix: true })}
                  </span>
                  <span className="capitalize">
                    {submission.type} submission
                  </span>
                </DialogDescription>
              </div>
              <div className="flex items-center gap-2">
                {getStatusBadge(submission.status)}
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full pr-4">
              {isEditing ? (
                <SubmissionEditor
                  submission={editedSubmission}
                  onChange={setEditedSubmission}
                />
              ) : (
                <div className="space-y-6">
                  {/* Basic Information */}
                  <div>
                    <h3 className="font-semibold mb-2">Basic Information</h3>
                    <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                      <div>
                        <span className="font-medium">Name: </span>
                        {submission.content?.name || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">Description: </span>
                        {submission.content?.description || 'N/A'}
                      </div>
                      {submission.content?.tags && (
                        <div>
                          <span className="font-medium">Tags: </span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {submission.content.tags.map((tag: string, index: number) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {submission.content?.difficulty && (
                        <div>
                          <span className="font-medium">Difficulty: </span>
                          <Badge variant="outline">{submission.content.difficulty}</Badge>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Manufacturer Suggestions */}
                  {submission.type === 'printer' && (
                    <div>
                      <h3 className="font-semibold mb-2 flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Manufacturer Suggestions ({manufacturerSuggestions.length})
                      </h3>
                      {loadingManufacturerSuggestions ? (
                        <div className="bg-muted/50 p-4 rounded-lg">
                          <div className="animate-pulse">Loading manufacturer suggestions...</div>
                        </div>
                      ) : manufacturerSuggestions.length === 0 ? (
                        <div className="bg-muted/50 p-4 rounded-lg text-muted-foreground">
                          No manufacturer suggestions found
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {manufacturerSuggestions.map((suggestion: any, index: number) => (
                            <div key={index} className="bg-muted/50 p-4 rounded-lg">
                              <div className="flex justify-between items-start mb-2">
                                <h4 className="font-medium">{suggestion.name}</h4>
                                <Badge variant="outline" className={
                                  suggestion.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                  suggestion.status === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                  'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                }>
                                  {suggestion.status}
                                </Badge>
                              </div>
                              {suggestion.manufacturer_name && (
                                <p className="text-sm text-muted-foreground mb-1">
                                  <strong>Manufacturer:</strong> {suggestion.manufacturer_name}
                                </p>
                              )}
                              {suggestion.series_name && (
                                <p className="text-sm text-muted-foreground mb-1">
                                  <strong>Series:</strong> {suggestion.series_name}
                                </p>
                              )}
                              {suggestion.build_plate_dimensions && (
                                <p className="text-sm text-muted-foreground mb-1">
                                  <strong>Build Plate:</strong> {suggestion.build_plate_dimensions}
                                </p>
                              )}
                              {suggestion.description && (
                                <p className="text-sm mt-2">{suggestion.description}</p>
                              )}
                              {suggestion.feedback && (
                                <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                                  <p className="text-sm text-red-700 dark:text-red-300">
                                    <strong>Feedback:</strong> {suggestion.feedback}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Content Details */}
                  {submission.type === 'tutorial' && submission.content?.sections && (
                    <div>
                      <h3 className="font-semibold mb-2">Tutorial Sections ({submission.content.sections.length})</h3>
                      <div className="space-y-3">
                        {submission.content.sections.map((section: any, index: number) => (
                          <div key={index} className="bg-muted/50 p-4 rounded-lg">
                            <h4 className="font-medium">{section.title}</h4>
                            <p className="text-sm text-muted-foreground mt-1">
                              {section.steps?.length || 0} steps
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* BOM (Bill of Materials) */}
                  {submission.content?.bom && submission.content.bom.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-2">Bill of Materials</h3>
                      <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                        {submission.content.bom.map((item: any, index: number) => (
                          <div key={index} className="flex justify-between items-center">
                            <span>{item.name}</span>
                            <Badge variant="outline">Qty: {item.quantity}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Feedback (if rejected) */}
                  {submission.feedback && (
                    <div>
                      <h3 className="font-semibold mb-2">Admin Feedback</h3>
                      <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                        <p className="text-sm">{submission.feedback}</p>
                      </div>
                    </div>
                  )}

                  {/* Raw Content (for debugging) */}
                  <details className="text-xs">
                    <summary className="cursor-pointer font-medium mb-2">Raw Content (Debug)</summary>
                    <pre className="bg-muted p-4 rounded-lg overflow-auto text-xs">
                      {JSON.stringify(submission.content, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </ScrollArea>
          </div>

          <Separator />

          <DialogFooter className="flex justify-between">
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              {submission.status === 'pending' && !isEditing && (
                <Button
                  variant="outline"
                  onClick={() => setIsEditing(true)}
                >
                  Edit Submission
                </Button>
              )}
            </div>

            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setIsEditing(false);
                      setEditedSubmission(submission);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSaveEdits}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </>
              ) : submission.status === 'pending' && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setActionDialog({ type: 'reject', feedback: '' })}
                    className="border-destructive text-destructive hover:bg-destructive/10"
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => setActionDialog({ type: 'approve', feedback: '' })}
                  >
                    Approve
                  </Button>
                </>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog open={!!actionDialog.type} onOpenChange={() => setActionDialog({ type: null, feedback: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog.type === 'approve' ? 'Approve' : 'Reject'} Submission
            </DialogTitle>
            <DialogDescription>
              {actionDialog.type === 'approve' 
                ? 'The submission will be approved and made available to users.' 
                : 'Please provide feedback on why the submission is being rejected.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Textarea
              placeholder={actionDialog.type === 'approve' 
                ? '(Optional) Add any comments or feedback'
                : 'Please provide rejection reason'}
              value={actionDialog.feedback}
              onChange={(e) => setActionDialog(prev => ({ ...prev, feedback: e.target.value }))}
              className="min-h-[100px]"
              required={actionDialog.type === 'reject'}
            />
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setActionDialog({ type: null, feedback: '' })}
            >
              Cancel
            </Button>
            <Button
              variant={actionDialog.type === 'approve' ? 'default' : 'destructive'}
              onClick={() => actionDialog.type && handleAction(actionDialog.type)}
              disabled={actionDialog.type === 'reject' && !actionDialog.feedback.trim()}
            >
              {actionDialog.type === 'approve' ? 'Approve' : 'Reject'} Submission
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SubmissionDetailModal;
